<!--
  Sector calendars — holiday periods no API publishes (bouwverlof, bouwvak), entered by a
  Storalytic super-admin and shared by every store that follows the calendar.

  Mounted by the super-admin page /sector_calendars (SectorCalendarsPage.vue); it never
  calls an API itself. The parent passes the entries and handles `add` / `remove`. Status and the
  reminder rule live in src/calendars/sectorCalendars.js.
-->
<template>
  <div class="sc-card">
    <div class="sc-head">
      <span class="sc-icon"><i class="bi bi-calendar2-week"></i></span>
      <div>
        <h2 class="sc-title">{{ $t('calendar.sector.title') }}</h2>
        <p class="sc-desc">
          {{ $t('calendar.sector.desc', { months: leadMonths }) }}
        </p>
      </div>
    </div>

    <div class="sc-body">
      <p v-if="!calendars.length" class="sc-empty">{{ $t('calendar.sector.empty') }}</p>

      <section v-for="cal in calendars" :key="cal.key" class="cal" :class="`state-${cal.state}`">
        <header class="cal-head">
          <div class="cal-title">
            <strong>{{ cal.title }}</strong>
            <span class="chip">{{ cal.country }}</span>
            <span class="cal-key">{{ cal.key }}</span>
          </div>
          <div class="cal-meta">
            <span class="followers">
              <i class="bi bi-shop"></i>
              {{ $t('calendar.sector.followers', { n: (stores[cal.key] || []).length }, (stores[cal.key] || []).length) }}
            </span>
            <span class="badge" :class="`b-${cal.state}`">{{ $t(`calendar.sector.state.${cal.state}`) }}</span>
          </div>
        </header>

        <div v-for="p in cal.periods" :key="p.period" class="period">
          <div class="period-line">
            <span class="period-name">{{ p.period }}</span>
            <span v-if="p.state === 'ok'" class="status ok">
              <i class="bi bi-check-circle"></i>
              {{ $t('calendar.sector.entered', { season: seasonLabel(p.last.StartDate, p.last.EndDate), date: fmtDate(p.reminderFrom, true, locale) }) }}
            </span>
            <span v-else class="status" :class="p.state">
              <i class="bi" :class="p.state === 'overdue' ? 'bi-exclamation-octagon' : 'bi-bell'"></i>
              {{ $t(p.state === 'overdue' ? 'calendar.sector.enterOverdue' : 'calendar.sector.enterDue', {
                season: seasonLabel(p.expectedStart, p.expectedEnd),
                date: fmtDate(p.expectedStart, true, locale),
                range: fmtRange(p.last.StartDate, p.last.EndDate, locale),
              }) }}
            </span>
            <button class="btn-link" type="button" @click="openAdd(cal, p)">
              <i class="bi bi-plus-lg"></i> {{ $t('calendar.sector.add', { season: seasonLabel(p.expectedStart, p.expectedEnd) }) }}
            </button>
          </div>

          <form v-if="adding === `${cal.key}|${p.period}`" class="add-form" @submit.prevent="submitAdd(cal, p)">
            <label>{{ $t('calendar.sector.from') }} <DateField v-model="draft.start" :aria-label="$t('calendar.sector.from')" /></label>
            <label>{{ $t('calendar.sector.to') }} <DateField v-model="draft.end" :min="draft.start" :aria-label="$t('calendar.sector.to')" /></label>
            <span class="hint">{{ $t('calendar.sector.prefillHint') }}</span>
            <button class="btn-solid" type="submit" :disabled="!draftValid">{{ $t('calendar.sector.save') }}</button>
            <button class="btn-link" type="button" @click="adding = null">{{ $t('calendar.sector.cancel') }}</button>
          </form>

          <ul class="history">
            <li v-for="x in [...p.entries].reverse()" :key="x.HolidayCalendarEntryId">
              <span class="season">{{ seasonLabel(x.StartDate, x.EndDate) }}</span>
              {{ fmtRange(x.StartDate, x.EndDate, locale) }}
              <button class="icon-btn" type="button" :title="$t('calendar.sector.remove', { period: p.period, season: seasonLabel(x.StartDate, x.EndDate) })"
                      @click="$emit('remove', x.HolidayCalendarEntryId)">
                <i class="bi bi-x-lg"></i>
              </button>
            </li>
          </ul>
        </div>

        <p v-if="(stores[cal.key] || []).length" class="store-list">
          {{ $t('calendar.sector.followedBy', { stores: stores[cal.key].join(', ') }) }}
        </p>
      </section>

      <!-- New calendar -->
      <button v-if="!creating" class="btn-outline" type="button" @click="openCreate">
        <i class="bi bi-plus-lg"></i> {{ $t('calendar.sector.newCalendar') }}
      </button>
      <form v-else class="create-form" @submit.prevent="submitCreate">
        <label class="wide">{{ $t('calendar.sector.calendarName') }}
          <input v-model="create.title" type="text" :placeholder="$t('calendar.sector.namePlaceholder')" required />
        </label>
        <label>{{ $t('calendar.sector.country') }}
          <input v-model="create.country" type="text" maxlength="2" placeholder="NL" required />
        </label>
        <label>{{ $t('calendar.sector.period') }}
          <input v-model="create.period" type="text" :placeholder="$t('calendar.sector.periodPlaceholder')" required />
        </label>
        <label>{{ $t('calendar.sector.from') }} <DateField v-model="create.start" :aria-label="$t('calendar.sector.from')" /></label>
        <label>{{ $t('calendar.sector.to') }} <DateField v-model="create.end" :min="create.start" :aria-label="$t('calendar.sector.to')" /></label>
        <span class="hint wide">{{ $t('calendar.sector.keyLabel') }} <code>{{ createKey || '—' }}</code> {{ $t('calendar.sector.keyHint') }}</span>
        <button class="btn-solid" type="submit" :disabled="!createValid">{{ $t('calendar.sector.create') }}</button>
        <button class="btn-link" type="button" @click="creating = false">{{ $t('calendar.sector.cancel') }}</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import DateField from '@/components/ui/DateField.vue'

import {
  groupCalendars, fmtDate, fmtRange, seasonLabel, REMINDER_LEAD_DAYS,
} from '@/calendars/sectorCalendars'

const props = defineProps({
  entries: { type: Array, required: true },
  today: { type: String, required: true },
  stores: { type: Object, default: () => ({}) },     // calendar key -> [store names]
})
const emit = defineEmits(['add', 'remove'])

// Month names in the panel follow the portal language (fmtDate defaults to English).
const { locale } = useI18n()

const leadMonths = Math.round(REMINDER_LEAD_DAYS / 30)
const calendars = computed(() => groupCalendars(props.entries, props.today))

const adding = ref(null)
const draft = reactive({ start: '', end: '' })
const draftValid = computed(() => draft.start && draft.end && draft.end >= draft.start)

function openAdd(cal, p) {
  adding.value = `${cal.key}|${p.period}`
  draft.start = p.expectedStart
  draft.end = p.expectedEnd
}

function submitAdd(cal, p) {
  emit('add', { calendar_key: cal.key, country: cal.country, name: `${cal.title} — ${p.period}`,
                start: draft.start, end: draft.end })
  adding.value = null
}

const creating = ref(false)
const create = reactive({ title: '', country: '', period: '', start: '', end: '' })
const createKey = computed(() => create.title.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48))
const createValid = computed(() => create.title && createKey.value.length > 1 && /^[A-Za-z]{2}$/.test(create.country)
  && create.period && create.start && create.end && create.end >= create.start)

function openCreate() {
  Object.assign(create, { title: '', country: '', period: '', start: '', end: '' })
  creating.value = true
}

function submitCreate() {
  emit('add', { calendar_key: createKey.value, country: create.country.toUpperCase(),
                name: `${create.title} — ${create.period}`, start: create.start, end: create.end })
  creating.value = false
}
</script>

<style scoped>
.sc-card { background: #fff; border: 1px solid #e6edf3; border-radius: 12px; box-shadow: 0 1px 2px rgba(13, 38, 76, 0.04); }
.sc-head { display: flex; gap: 10px; padding: 13px 16px 12px; border-bottom: 1px solid #f0f3f7; }
.sc-icon { color: #4F7EB3; font-size: 1.05rem; padding-top: 2px; }
.sc-title { margin: 0; font-weight: 700; font-size: 0.98rem; color: #2b3a45; }
.sc-desc { margin: 2px 0 0; color: #7c8894; font-size: 0.82rem; line-height: 1.4; max-width: 70ch; }
.sc-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 14px; }
.sc-empty { color: #7c8894; margin: 0; }

.cal { border: 1px solid #e6edf3; border-left: 3px solid #6C9046; border-radius: 10px; padding: 10px 12px; }
.cal.state-due { border-left-color: #E0A100; }
.cal.state-overdue { border-left-color: #C21500; }
.cal-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.cal-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; color: #2b3a45; }
.cal-key { font-family: ui-monospace, monospace; font-size: 0.75rem; color: #9aa6b1; }
.chip { font-size: 0.72rem; background: #eef3f8; color: #425461; border-radius: 6px; padding: 1px 6px; }
.cal-meta { display: flex; align-items: center; gap: 10px; font-size: 0.82rem; color: #6b7a86; }
.badge { font-size: 0.75rem; border-radius: 999px; padding: 2px 9px; font-weight: 600; }
.b-ok { background: #edf5e6; color: #4d6b2f; }
.b-due { background: #fff4d6; color: #8a6200; }
.b-overdue { background: #fde8e5; color: #a01200; }

.period { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #edf1f5; }
.period-line { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; font-size: 0.86rem; }
.period-name { font-weight: 600; color: #2b3a45; min-width: 64px; }
.status { color: #55606b; }
.status.ok { color: #4d6b2f; }
.status.due { color: #8a6200; }
.status.overdue { color: #a01200; }
.status .bi { margin-right: 3px; }
.history { list-style: none; margin: 6px 0 0 74px; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.history li { font-size: 0.8rem; color: #55606b; background: #f6f8fa; border-radius: 6px; padding: 2px 4px 2px 8px; display: flex; align-items: center; gap: 6px; }
.season { font-weight: 600; color: #425461; }
.store-list { margin: 10px 0 0; font-size: 0.8rem; color: #7c8894; }

.add-form, .create-form { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 8px 0 0 74px; font-size: 0.84rem; }
.create-form { margin-left: 0; border: 1px dashed #cdd8e3; border-radius: 10px; padding: 10px 12px; }
.add-form label, .create-form label { display: flex; flex-direction: column; gap: 2px; color: #55606b; font-size: 0.78rem; }
.create-form .wide { flex-basis: 100%; }
.add-form input, .create-form input { border: 1px solid #d5dde5; border-radius: 6px; padding: 4px 6px; font-size: 0.85rem; }
.hint { font-size: 0.76rem; color: #8a96a1; }

.btn-solid { background: #6C9046; color: #fff; border: 0; border-radius: 8px; padding: 6px 12px; cursor: pointer; }
.btn-solid:disabled { background: #c9cfd6; cursor: not-allowed; }
.btn-outline { align-self: flex-start; display: inline-flex; gap: 6px; align-items: center; border: 1px solid #4F7EB3; color: #4F7EB3; background: #f7fbff; border-radius: 8px; padding: 6px 12px; cursor: pointer; }
.btn-link { background: none; border: 0; color: #4F7EB3; cursor: pointer; padding: 0; font-size: 0.84rem; }
.icon-btn { background: none; border: 0; color: #9aa6b1; cursor: pointer; padding: 0 2px; }
.icon-btn:hover { color: #C21500; }

@media (max-width: 640px) {
  .history, .add-form { margin-left: 0; }
}
</style>
